// /settings: the dealer's brand on the QuoteLink and PDF, its people and roles,
// and how it prices. For Phoneware: the Phoneware team with each person's tier (mock M1).
// /settings/activity (mock M1): every time Phoneware acted as someone, and what it changed.
// /settings/connect and /settings/apps (mock M8): connecting Claude, ChatGPT or a
// dealer's own system through Omni's OAuth sign-in, and the apps that work as someone.
import React, { useRef, useState } from "react";
import {
  ADMIN_TIERS,
  DEALER_ROLES,
  OMNI_OAUTH_AUDIENCE_V1,
  OMNI_OAUTH_ISSUER,
  V1_ROUTES,
  assertRoleEmail,
  isAdminRole,
  isDealerRole,
  roleLabel,
  type AdminLensUser,
  type AdminTier,
  type Dealer,
  type DealerRole,
  type InviteRequest,
  type LensUser,
  type Me,
  type OAuthGrantRecord,
  type PersonAccounts,
  type UserScope,
} from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { Icon, type IconName } from "../icons.js";
import { Link, navigate, type SettingsTab } from "../router.js";
import { useLoad, useSession, viewerKind, type Loaded, type ViewerKind } from "../session.js";
import { errorText, firstName, initials, isEmail, plural, whenIn } from "../format.js";
import { CANT_DO, MCP_URL, REVOKED_BY_REFRESH_REUSE, WRITE_SCOPE, orderGrants, readScope, roleAt } from "../api-oauth.js";
import { AppReq, AppTile } from "./connect-bits.js";
import { Avatar, Callout, Chip, DealerMark, Head, Sheet, TIER_BLURB, TIER_ICON, Tier, Wordmark, useTitle, useToast } from "../ui.js";
import { ActivityTab } from "./Activity.js";
import { AccountsCell, AccountGroups, PRODUCT, SummaryChips, accountCounts, numberWord, signsInThere, useAccountsOf, type RowActions } from "./AccountRows.js";
import { PersonSheet } from "./PersonSheet.js";
import { ActingSignInNote, AddMicrosoftSheet, SignInHidden, SignInTab } from "./SignInMethods.js";

const SWATCHES = ["#1F4D3A", "#8A3B12", "#24526B", "#5A2E6B", "#3C3C46", "#7A1F2B"];
const SCOPES: Array<[UserScope, string]> = [
  ["all", "All customers"],
  ["own", "Own customers"],
];
const LOGO_LIMIT = 200 * 1024;

function BrandPreview({ dealer }: { dealer: Dealer }): React.ReactElement {
  const color = dealer.brand.color;
  return (
    <div className="brand-prev">
      <figure className="prev-phone">
        <div className="pp-top" style={{ background: color }}>
          <DealerMark name={dealer.name} brand={dealer.brand} size={18} onColor />
          <b>{dealer.name}</b>
        </div>
        <div className="pp-body">
          <span className="pp-id">Proposal</span>
          <b>Proposal for your customer</b>
          <i />
          <i />
          <i className="short" />
          <span className="pp-btn" style={{ background: color }}>
            Accept and sign
          </span>
          <span className="pp-pw">
            Powered by <Wordmark className="wm-xxs" />
          </span>
        </div>
        <figcaption>QuoteLink</figcaption>
      </figure>
      <figure className="prev-pdf">
        <div className="pdf-mini" style={{ "--brand": color } as React.CSSProperties}>
          <div className="pdf-band" />
          <div className="pm-head">
            <DealerMark name={dealer.name} brand={dealer.brand} size={18} />
            <b>{dealer.name}</b>
          </div>
          <i />
          <i />
          <i className="short" />
          <div className="pm-sign" />
        </div>
        <figcaption>PDF</figcaption>
      </figure>
    </div>
  );
}

/** "Omni needs at least one active Super admin": the server's 409 when a change would leave none. */
const LAST_SUPER = "Omni needs at least one active Super admin";

/** A refusal with its title and why, as the invite and tier sheets show it (mock M1). */
interface Refusal {
  title: string;
  text: string;
}

/** The four Phoneware tiers as radio cards, each with its mark and what it can do. */
function TierPicker({ value, onChange }: { value: AdminTier; onChange: (t: AdminTier) => void }): React.ReactElement {
  return (
    <fieldset className="tierpick" data-testid="tier-picker">
      <legend>Tier</legend>
      {ADMIN_TIERS.map((t) => (
        <label key={t}>
          <input type="radio" name="tier" value={t} checked={value === t} data-testid={`tier-pick-${t}`} onChange={() => onChange(t)} />
          <span>
            <b>
              <Icon name={TIER_ICON[t]} size={14} />
              {roleLabel(t)}
            </b>
            <span>{TIER_BLURB[t]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function RefusalNote({ refusal, testId }: { refusal: Refusal; testId: string }): React.ReactElement {
  return (
    <div className="callout stop titled" role="alert" data-testid={testId}>
      <Icon name="triangle-alert" size={15} />
      <span>
        <b>{refusal.title}</b>
        {refusal.text}
      </span>
    </div>
  );
}

/**
 * Invite someone. An owner invites people to their own dealer. A Super admin (users.manage at any dealer) picks where
 * they work: the Phoneware team, with a tier, or a dealer, with a role. Only a phoneware.us email can hold a tier; the
 * server refuses anything else with 403, and the sheet says why in the mock's words.
 */
function InviteSheet({ dealers, onInvited, onClose }: { dealers: Dealer[] | null; onInvited: (u: LensUser) => void; onClose: () => void }): React.ReactElement {
  const { me, api, can, writeScope } = useSession();
  const anyDealer = writeScope("users.manage") === "any";
  const tiers = can("admins.manage");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // "" is the Phoneware team, for whoever can grant a tier.
  const [dealerId, setDealerId] = useState(anyDealer ? (tiers ? "" : dealers?.[0]?.id ?? "") : (me.user.dealerId ?? ""));
  const [tier, setTier] = useState<AdminTier>("admin_support");
  const [role, setRole] = useState<DealerRole>("sales");
  const [scope, setScope] = useState<UserScope>("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const forTeam = tiers && dealerId === "";
  const ready = name.trim() !== "" && isEmail(email) && (forTeam || dealerId !== "");

  async function invite(): Promise<void> {
    const address = email.trim().toLowerCase();
    const input: InviteRequest = forTeam
      ? { email: address, name: name.trim(), role: tier, scope: "all", dealerId: null }
      : { email: address, name: name.trim(), role, scope, dealerId };
    setBusy(true);
    setError(null);
    setRefusal(null);
    try {
      onInvited(await api.invite(input));
    } catch (err) {
      const offDomain = (() => {
        try {
          assertRoleEmail(tier, address);
          return false;
        } catch {
          return true;
        }
      })();
      if (forTeam && err instanceof ApiError && err.status === 403 && offDomain) {
        setRefusal({ title: "Phoneware admins need a phoneware.us email", text: ` ${address} is not one. A dealer\u2019s people are invited to their dealer instead.` });
      } else {
        setError(errorText(err));
      }
      setBusy(false);
    }
  }

  return (
    <Sheet
      title={forTeam ? "Invite to the Phoneware team" : "Invite someone"}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="send-invite" disabled={!ready || busy} onClick={() => void invite()}>
            <Icon name="user-plus" size={15} />
            {busy ? "Inviting" : "Invite"}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="card-p">They sign in to Omni with a link sent to this email. Nobody else can.</p>
        <label className="field">
          <span>Name</span>
          <input className="input" autoComplete="off" data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>Email</span>
          <input
            className={refusal ? "input refused" : "input"}
            type="email"
            autoComplete="off"
            placeholder={forTeam ? "name@phoneware.us" : undefined}
            aria-invalid={refusal ? true : undefined}
            data-testid="invite-email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setRefusal(null);
            }}
          />
        </label>
        {refusal ? <RefusalNote refusal={refusal} testId="invite-refusal" /> : null}
        {anyDealer ? (
          <label className="field">
            <span>Works at</span>
            <select
              className="input"
              data-testid="invite-works-at"
              value={dealerId}
              onChange={(e) => {
                setDealerId(e.target.value);
                setRefusal(null);
              }}
            >
              {tiers ? <option value="">The Phoneware team</option> : null}
              {(dealers ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {forTeam ? (
          <TierPicker value={tier} onChange={setTier} />
        ) : (
          <div className="qfields">
            <label className="field">
              <span>Role</span>
              <select className="input" value={role} onChange={(e) => setRole(DEALER_ROLES.find((r) => r === e.target.value) ?? role)}>
                {DEALER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Sees</span>
              <select className="input" value={scope} onChange={(e) => setScope(e.target.value as UserScope)}>
                {SCOPES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

/** A people row the reader may open: their own goes to Your accounts, anyone else's opens the person sheet. */
function openRow(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, self: boolean, open: () => void): void {
  if ("key" in e && e.key !== "Enter") return;
  if (e.target instanceof Element && e.target.closest("a, button, input, select, textarea, label")) return;
  if (self) navigate("/settings/accounts");
  else open();
}

function PeopleTable({ users, editable, dealerName, onChange }: { users: LensUser[]; editable: boolean; dealerName: string; onChange: (u: LensUser) => void }): React.ReactElement {
  const { me, api } = useSession();
  const toast = useToast();
  const [problem, setProblem] = useState<string | null>(null);
  const [person, setPerson] = useState<LensUser | null>(null);
  // The owner reads the accounts of people at their dealer (users.manage); everyone else reads their own.
  const accounts = useAccountsOf(users.map((u) => u.email));

  async function patch(u: LensUser, change: Partial<Pick<LensUser, "role" | "scope" | "status">>): Promise<void> {
    setProblem(null);
    try {
      const next = await api.updateUser(u.email, change);
      onChange(next);
      toast(`${next.name} updated.`);
    } catch (err) {
      setProblem(errorText(err));
    }
  }

  return (
    <>
      {problem ? (
        <Callout kind="stop" icon="triangle-alert">
          {problem}
        </Callout>
      ) : null}
      <div className="tablewrap">
        <table className="t t-people t-dealer-people">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Sees</th>
              <th>Status</th>
              <th>Accounts</th>
              {editable ? (
                <th>
                  <span className="sr">Open</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const self = u.email === me.user.email;
              const canEdit = editable && !self && isDealerRole(u.role);
              const opens = editable ? (self ? { "data-go": "/settings/accounts" } : { "data-person": u.email }) : {};
              return (
                <tr
                  key={u.email}
                  data-testid={`person-row-${u.email}`}
                  {...opens}
                  tabIndex={editable ? 0 : undefined}
                  onClick={editable ? (e) => openRow(e, self, () => setPerson(u)) : undefined}
                  onKeyDown={editable ? (e) => openRow(e, self, () => setPerson(u)) : undefined}
                >
                  <td>
                    <span className="person">
                      <Avatar name={u.name} />
                      <span>
                        <b>
                          {u.name}
                          {self ? <span className="you"> (you)</span> : null}
                        </b>
                        <span className="sub">{u.email}</span>
                      </span>
                    </span>
                  </td>
                  <td data-label="Role">
                    {canEdit ? (
                      <select className="input slim" aria-label={`Role for ${u.name}`} value={u.role} onChange={(e) => void patch(u, { role: DEALER_ROLES.find((r) => r === e.target.value) ?? u.role })}>
                        {DEALER_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {roleLabel(r)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      roleLabel(u.role)
                    )}
                  </td>
                  <td data-label="Sees">
                    {canEdit ? (
                      <select className="input slim" aria-label={`What ${u.name} sees`} value={u.scope} onChange={(e) => void patch(u, { scope: e.target.value as UserScope })}>
                        {SCOPES.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    ) : isAdminRole(u.role) ? (
                      "Every dealer"
                    ) : (
                      SCOPES.find(([s]) => s === u.scope)?.[1]
                    )}
                  </td>
                  <td data-label="Status">
                    {u.status === "active" ? (
                      <Chip kind="ok">Active</Chip>
                    ) : u.status === "invited" ? (
                      <Chip kind="quiet">Invited</Chip>
                    ) : (
                      <Chip kind="stop">Turned off</Chip>
                    )}
                    {canEdit ? (
                      <button className="link" type="button" onClick={() => void patch(u, { status: u.status === "disabled" ? "active" : "disabled" })}>
                        {u.status === "disabled" ? " Turn on" : " Turn off"}
                      </button>
                    ) : null}
                  </td>
                  <td data-label="Accounts">
                    <AccountsCell accounts={accounts[u.email]} />
                  </td>
                  {editable ? (
                    <td className="num open">
                      <Icon name="chevron-right" size={15} className="chev" />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {person ? <PersonSheet person={person} dealerName={dealerName} onClose={() => setPerson(null)} /> : null}
    </>
  );
}

function DealerSettings({ dealer, tabs }: { dealer: Dealer; tabs: React.ReactNode }): React.ReactElement {
  const { me, api, refreshMe, writeScope } = useSession();
  const toast = useToast();
  // The owner manages people at their dealer (users.manage); everyone else reads themselves.
  const owner = writeScope("users.manage") === "own";
  const users = useLoad<LensUser[]>(() => (owner ? api.users() : Promise.resolve([me.user])), [owner]);
  const [inviting, setInviting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [color, setColor] = useState(dealer.brand.color);
  const file = useRef<HTMLInputElement>(null);
  const shown: Dealer = { ...dealer, brand: { ...dealer.brand, color } };

  async function saveBrand(next: Dealer["brand"], message: string): Promise<void> {
    setProblem(null);
    try {
      await api.updateDealer(dealer.id, { brand: next });
      refreshMe();
      toast(message);
    } catch (err) {
      setColor(dealer.brand.color);
      setProblem(errorText(err));
    }
  }

  function pickColor(next: string): void {
    setColor(next);
    void saveBrand({ ...dealer.brand, color: next }, "Brand color saved. Every new QuoteLink and PDF uses it.");
  }

  function readLogo(e: React.ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    if (picked.size > LOGO_LIMIT) {
      setProblem("Use a logo under 200 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") void saveBrand({ ...dealer.brand, color, logoDataUrl: reader.result }, "Logo saved.");
    };
    reader.readAsDataURL(picked);
  }

  return (
    <>
      <Head
        title="Settings"
        sub={settingsSub("dealer", dealer, !!me.actingAs)}
        actions={
          owner ? (
            <button className="btn primary" type="button" onClick={() => setInviting(true)}>
              <Icon name="user-plus" size={15} />
              Invite
            </button>
          ) : null
        }
      />
      {tabs}
      {me.actingAs ? <ActingSignInNote name={me.actingAs.target.name} /> : null}
      {problem ? (
        <Callout kind="stop" icon="triangle-alert">
          {problem}
        </Callout>
      ) : null}
      <section className="card brandpanel">
        <div className="card-h">
          <h2>Your brand</h2>
          <span className="note">On every QuoteLink and PDF</span>
        </div>
        <div className="brand-grid">
          <div className="brand-side">
            <div className="brand-logo">
              <DealerMark name={dealer.name} brand={shown.brand} size={44} />
              <div>
                <b>{dealer.name}</b>
                {owner ? (
                  <>
                    <button className="link" type="button" onClick={() => file.current?.click()}>
                      {dealer.brand.logoDataUrl ? "Replace logo" : "Add a logo"}
                    </button>
                    <input ref={file} className="sr" type="file" accept="image/png,image/svg+xml" onChange={readLogo} />
                  </>
                ) : null}
              </div>
            </div>
            <div className="field">
              <span id="sw-l">Color</span>
              <div className="swatches" role="radiogroup" aria-labelledby="sw-l">
                {SWATCHES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={color.toLowerCase() === s.toLowerCase()}
                    aria-label={`Color ${s}`}
                    disabled={!owner}
                    style={{ background: s }}
                    onClick={() => pickColor(s)}
                  />
                ))}
                {owner ? (
                  <input
                    className="swatch-pick"
                    type="color"
                    aria-label="Pick any color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    onBlur={() => {
                      if (color !== dealer.brand.color) pickColor(color);
                    }}
                  />
                ) : null}
              </div>
            </div>
            <p className="card-note">
              The customer sees your name and color. Phoneware shows only as a small Powered by mark at the foot.{owner ? "" : " The owner at your dealer can change them."}
            </p>
          </div>
          <BrandPreview dealer={shown} />
        </div>
      </section>
      <section className="card" data-testid="dealer-people">
        <div className="card-h">
          <h2>People and roles</h2>
          {owner ? <span className="note">Open someone to see their accounts</span> : null}
        </div>
        {users.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {users.error.message}
          </Callout>
        ) : users.data ? (
          <PeopleTable
            users={users.data}
            editable={owner}
            dealerName={dealer.name}
            onChange={(u) => users.set((users.data ?? []).map((x) => (x.email === u.email ? u : x)))}
          />
        ) : (
          <div className="empty">Loading people.</div>
        )}
        <p className="card-note">
          Owners manage people and the brand. Sales quotes. Tech sees customers and orders but not prices.
          {owner ? "" : " Your owner and Phoneware can see each person\u2019s accounts."}
        </p>
      </section>
      <section className="card">
        <div className="card-h">
          <h2>Model</h2>
          <Chip>{dealer.model === "agent" ? "Dealer" : "Reseller"}</Chip>
        </div>
        {dealer.model === "agent" ? (
          <p className="card-p">
            A dealer sells at Phoneware&rsquo;s prices and is paid commission. You quote Phoneware&rsquo;s list price and can discount down to each product&rsquo;s floor. Anything below a floor goes to Phoneware for approval before the
            customer sees it.
          </p>
        ) : (
          <p className="card-p">
            A reseller buys at a flat cost and marks up; Phoneware still bills both. You see your buy price from Phoneware and quote your own price. There is no approval floor: nothing waits on Phoneware. Omni warns you when a line sells
            below what it costs you.
          </p>
        )}
        <p className="card-note">
          <Link to="/catalog">{dealer.model === "agent" ? "Every list price and floor is in the catalog" : "Your buy prices are in the catalog"}</Link>
        </p>
      </section>
      {inviting ? (
        <InviteSheet
          dealers={null}
          onClose={() => setInviting(false)}
          onInvited={(u) => {
            setInviting(false);
            users.set([...(users.data ?? []), u]);
            toast(`Invited ${u.name}. They sign in with an email link.`);
          }}
        />
      ) : null}
    </>
  );
}

/** Change tier: the person, the server's 409 when they are the last active Super admin, and the four tiers. */
function TierSheet({ user, onSaved, onClose }: { user: AdminLensUser; onSaved: (u: LensUser) => void; onClose: () => void }): React.ReactElement {
  const { api } = useSession();
  const [tier, setTier] = useState<AdminTier>(user.role);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    if (tier === user.role) {
      onClose();
      return;
    }
    setBusy(true);
    setRefusal(null);
    setError(null);
    try {
      onSaved(await api.updateUser(user.email, { role: tier }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setRefusal({ title: LAST_SUPER, text: ` ${user.name} is the only one. Make someone else a Super admin first, then change this tier.` });
      } else {
        setError(errorText(err));
      }
      setBusy(false);
    }
  }

  return (
    <Sheet
      title="Change tier"
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="save-tier" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving" : "Save tier"}
          </button>
        </>
      }
    >
      <div className="stack">
        <span className="person">
          <Avatar name={user.name} />
          <span>
            <b>{user.name}</b>
            <span className="sub">
              {user.email} · {roleLabel(user.role)}
            </span>
          </span>
        </span>
        {refusal ? <RefusalNote refusal={refusal} testId="tier-refusal" /> : null}
        <TierPicker value={tier} onChange={setTier} />
        <p className="card-note">The new tier applies to their next request. Every tier sees every dealer.</p>
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

/**
 * The Phoneware side of Settings (mock M1): the Phoneware team with each person's tier, and what each tier can do.
 * Super admins invite, change tiers and turn people off; the server keeps one active Super admin and answers 409.
 */
function AdminSettings({ tabs }: { tabs: React.ReactNode }): React.ReactElement {
  const { me, api, can, writeScope } = useSession();
  const toast = useToast();
  const manage = can("admins.manage");
  const invites = writeScope("users.manage") === "any";
  const users = useLoad<LensUser[]>(() => api.users(), []);
  const dealers = useLoad<Dealer[]>(() => (invites ? api.dealers() : Promise.resolve([])), [invites]);
  const [inviting, setInviting] = useState(false);
  const [changing, setChanging] = useState<AdminLensUser | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [person, setPerson] = useState<LensUser | null>(null);
  const team = (users.data ?? []).filter((u): u is AdminLensUser => isAdminRole(u.role));
  const replace = (u: LensUser) => users.set((users.data ?? []).map((x) => (x.email === u.email ? u : x)));
  // Every Phoneware tier reads anyone's accounts (spec 3.6).
  const accounts = useAccountsOf(team.map((u) => u.email));

  async function toggle(u: AdminLensUser): Promise<void> {
    setRefusal(null);
    setProblem(null);
    const status = u.status === "disabled" ? "active" : "disabled";
    try {
      const next = await api.updateUser(u.email, { status });
      replace(next);
      toast(status === "disabled" ? `${next.name} is turned off. They can\u2019t sign in.` : `${next.name} is turned on.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setRefusal({ title: LAST_SUPER, text: ` ${u.name} is the only one, so Omni kept them on. Make someone else a Super admin first.` });
      } else {
        setProblem(errorText(err));
      }
    }
  }

  const statusChip = (u: LensUser) =>
    u.status === "active" ? <Chip kind="ok">Active</Chip> : u.status === "invited" ? <Chip kind="quiet">Invited</Chip> : <Chip kind="stop">Turned off</Chip>;
  const actions = (u: AdminLensUser) => (
    <>
      <button className="link" type="button" data-testid={`tier-change-${u.email}`} onClick={() => setChanging(u)}>
        Change tier
      </button>
      <button className="link" type="button" data-testid={`tier-toggle-${u.email}`} onClick={() => void toggle(u)}>
        {u.status === "disabled" ? "Turn on" : "Turn off"}
      </button>
    </>
  );

  return (
    <>
      <Head
        title="Settings"
        sub={settingsSub("admin", null)}
        actions={
          invites ? (
            <button className="btn primary" type="button" data-testid="team-invite" onClick={() => setInviting(true)}>
              <Icon name="user-plus" size={15} />
              Invite
            </button>
          ) : null
        }
      />
      {tabs}
      {refusal ? <RefusalNote refusal={refusal} testId="team-refusal" /> : null}
      {problem ? (
        <Callout kind="stop" icon="triangle-alert">
          {problem}
        </Callout>
      ) : null}
      <div className="stack">
        <section className="card" data-testid="team">
          <div className="card-h">
            <h2>Phoneware team</h2>
            <span className="note">Open someone to see their accounts</span>
          </div>
          {users.error ? (
            <Callout kind="stop" icon="triangle-alert">
              {users.error.message}
            </Callout>
          ) : !users.data ? (
            <div className="empty">Loading people.</div>
          ) : team.length === 0 ? (
            <div className="empty">Nobody on the Phoneware team yet.</div>
          ) : (
            <div className="tablewrap">
              <table className="t t-people t-team">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className="hide-sm">Tier</th>
                    <th className="hide-sm">Status</th>
                    <th className="hide-sm">Accounts</th>
                    {manage ? (
                      <th className="hide-sm">
                        <span className="sr">Change</span>
                      </th>
                    ) : null}
                    <th>
                      <span className="sr">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((u) => {
                    const self = u.email === me.user.email;
                    return (
                      <tr
                        key={u.email}
                        data-testid={`team-row-${u.email}`}
                        {...(self ? { "data-go": "/settings/accounts" } : { "data-person": u.email })}
                        tabIndex={0}
                        onClick={(e) => openRow(e, self, () => setPerson(u))}
                        onKeyDown={(e) => openRow(e, self, () => setPerson(u))}
                      >
                        <td>
                          <span className="person">
                            <span className={self ? "av azure" : "av"} aria-hidden="true">
                              {initials(u.name)}
                            </span>
                            <span>
                              <b>
                                {u.name}
                                {self ? <span className="tag">You</span> : null}
                              </b>
                              <span className="sub">{u.email}</span>
                              {/* At 390 the table is one column: tier, status and the row's actions sit under the name. */}
                              <span className="only-sm team-sm">
                                <Tier tier={u.role} />
                                {u.status === "active" ? null : statusChip(u)}
                                {manage ? <span className="acts-sm">{actions(u)}</span> : null}
                              </span>
                            </span>
                          </span>
                        </td>
                        <td className="hide-sm">
                          <Tier tier={u.role} />
                        </td>
                        <td className="hide-sm">{statusChip(u)}</td>
                        <td className="hide-sm">
                          <AccountsCell accounts={accounts[u.email]} />
                        </td>
                        {manage ? <td className="num acts hide-sm">{actions(u)}</td> : null}
                        <td className="num">
                          <Icon name="chevron-right" size={15} className="chev" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="card-note">{manage ? "Only a phoneware.us email can hold a tier. Omni always keeps one active Super admin." : "Super admins manage the Phoneware team."}</p>
        </section>
        <section className="card" data-testid="tiers">
          <div className="card-h">
            <h2>Tiers</h2>
            <span className="note">What each one can do</span>
          </div>
          <ul className="tiers">
            {ADMIN_TIERS.map((t) => (
              <li key={t}>
                <span className="row-ic">
                  <Icon name={TIER_ICON[t]} />
                </span>
                <span>
                  <b>{roleLabel(t)}</b>
                  <span>{TIER_BLURB[t]}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      {inviting ? (
        <InviteSheet
          dealers={dealers.data ? [...dealers.data].sort((a, b) => a.name.localeCompare(b.name)) : null}
          onClose={() => setInviting(false)}
          onInvited={(u) => {
            setInviting(false);
            users.set([...(users.data ?? []), u]);
            toast(isAdminRole(u.role) ? `Invited ${u.name} as ${roleLabel(u.role)}. They sign in with an email link.` : `Invited ${u.name}. They sign in with an email link.`);
          }}
        />
      ) : null}
      {changing ? (
        <TierSheet
          user={changing}
          onClose={() => setChanging(null)}
          onSaved={(u) => {
            setChanging(null);
            replace(u);
            toast(`${u.name} is now ${roleLabel(u.role)}.`);
          }}
        />
      ) : null}
      {person ? <PersonSheet person={person} dealerName={null} onClose={() => setPerson(null)} /> : null}
    </>
  );
}

// ------------------------------------------------------------------ AI and app access (M8)

function settingsSub(kind: ViewerKind, dealer: Dealer | null, acting = false): string {
  switch (kind) {
    case "admin":
      return "Phoneware\u2019s side of Omni, how you sign in, and the AI and apps that work as you.";
    case "dealer":
      // While acting, how the person signs in and the apps that work as them are theirs alone.
      return acting
        ? `${dealer?.name ?? "Your dealer"}. Your brand, your people and how you price.`
        : `${dealer?.name ?? "Your dealer"}. Your brand, your people and how you price, how you sign in, and the AI and apps that work as you.`;
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
}

/** The Settings tabs in spec 3.9's order. Connected apps counts what is live. */
function SettingsTabs({ tab, general, live, acting }: { tab: SettingsTab; general: string; live: number | null; acting: boolean }): React.ReactElement {
  const items: Array<[SettingsTab, string, string, number | null]> = [
    ["general", "/settings", general, null],
    ["activity", "/settings/activity", "Phoneware activity", null],
    // How someone signs in is theirs alone: hidden while a Super admin acts as them (spec 3.9).
    ...(acting ? [] : [["signin", "/settings/signin", "Sign-in", null] satisfies [SettingsTab, string, string, number | null]]),
    // No count: while linking from Edge isn't available (#1660), nothing on Your accounts waits on the reader.
    ["accounts", "/settings/accounts", "Your accounts", null],
    ["connect", "/settings/connect", "Connect an AI or app", null],
    ["apps", "/settings/apps", "Connected apps", live],
  ];
  return (
    <nav className="tabs settings-tabs" aria-label="Settings">
      {items.map(([id, to, label, n]) => (
        <Link key={id} to={to} aria-current={tab === id ? "page" : undefined} data-testid={`tab-${id}`}>
          {label}
          {n !== null ? <span className="n">{n}</span> : null}
        </Link>
      ))}
    </nav>
  );
}

/** Connect an AI while acting as someone (mock M1 state 56): a grant made now would outlive the session. */
function ConnectOff({ name }: { name: string }): React.ReactElement {
  return (
    <section className="card" data-testid="connect-off">
      <div className="callout warn titled">
        <Icon name="lock" size={15} />
        <span>
          <b>Connect an AI is off while you act as {name}</b>
          An AI connected now would keep working as {firstName(name)} after you stop, so Omni refuses it. Stop first, then connect your own AI as yourself.
        </span>
      </div>
    </section>
  );
}

// The steps follow Claude's and ChatGPT's help pages as of 2026-10-17.
const HOWTO: Record<"claude" | "chatgpt", Array<{ step: React.ReactNode; aside?: string }>> = {
  claude: [
    {
      step: (
        <>
          In Claude, open <b>Customize</b>, then <b>Connectors</b>, and choose <b>Add custom connector</b>.
        </>
      ),
      aside: "On a Team or Enterprise plan, an Owner of your Claude organization adds it under Organization settings, Connectors. You then find Omni there and choose Connect.",
    },
    {
      step: (
        <>
          Name it <b>Omni</b> and paste the address. If Claude asks how to sign in, choose <b>Sign in now</b>, and for the OAuth client choose <b>Register automatically</b>.
        </>
      ),
    },
    {
      step: (
        <>
          Choose <b>Add</b>, then <b>Connect</b>. Omni opens, asks you to sign in if you aren&rsquo;t, and asks whether Claude can act as you. Choose <b>Allow</b>.
        </>
      ),
    },
    {
      step: (
        <>
          In a chat, choose <b>+</b>, then <b>Connectors</b>, and turn Omni on.
        </>
      ),
    },
  ],
  chatgpt: [
    {
      step: (
        <>
          On chatgpt.com, open <b>Settings</b>, then <b>Security and login</b>, and turn on <b>Developer mode</b>.
        </>
      ),
      aside: "Plus, Pro, Business, Enterprise and Education plans have it. On a workspace, an admin may have to allow it.",
    },
    {
      step: (
        <>
          Open <b>Plugins</b>, choose <b>+</b>, then <b>Add custom MCP server</b>.
        </>
      ),
    },
    {
      step: (
        <>
          Name it <b>Omni</b>, paste the address under Connection, and choose <b>OAuth</b> for authentication.
        </>
      ),
    },
    {
      step: (
        <>
          Accept the warning and create it. Omni opens and asks whether ChatGPT can act as you. Choose <b>Allow</b>.
        </>
      ),
    },
    {
      step: (
        <>
          In a chat, type <b>@</b> and pick Omni.
        </>
      ),
    },
  ],
};

function CanDo({ icon, tone, title, detail }: { icon: IconName; tone: "yes" | "no"; title: string; detail?: string }): React.ReactElement {
  return (
    <li>
      <Icon name={icon} size={16} className={tone} />
      <span>
        <b>{title}</b>
        {detail ? <span className="m">{detail}</span> : null}
      </span>
    </li>
  );
}

function ConnectTab(): React.ReactElement {
  const { me, writeScope } = useSession();
  const toast = useToast();
  const [howto, setHowto] = useState<"claude" | "chatgpt">("claude");
  const [docs, setDocs] = useState(false);
  const dealer = me.dealer;
  const kind = viewerKind(me.user);
  let who: string;
  switch (kind) {
    case "admin":
      who =
        writeScope("grants.revoke") === "any"
          ? "You can revoke it, and any app working as anyone, under Connected apps."
          : "You and any Super admin at Phoneware can revoke it under Connected apps.";
      break;
    case "dealer": {
      const name = dealer?.name ?? "your dealer";
      who =
        writeScope("grants.revoke") === "own" && writeScope("users.manage") === "own"
          ? `You see and can revoke every app that works as someone at ${name}, under Connected apps.`
          : `You, the owner at ${name} and a Super admin at Phoneware can revoke it under Connected apps.`;
      break;
    }
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }

  function copy(): void {
    const byHand = () => toast(`Select the address and copy it: ${MCP_URL}`);
    const clipboard = window.navigator.clipboard as Clipboard | undefined;
    if (!clipboard) {
      byHand();
      return;
    }
    clipboard.writeText(MCP_URL).then(() => toast(`Copied ${MCP_URL}`), byHand);
  }

  return (
    <div className="connect-grid">
      <section className="card" data-testid="connect-card">
        <div className="card-h">
          <h2>Connect an AI or app</h2>
        </div>
        <p className="lede">
          Claude, ChatGPT or your own system can work in Omni as you. It signs in with your Omni email link, sees what you see, and makes drafts for you to check and send.
        </p>
        <span className="label connect-label">Omni&rsquo;s MCP address</span>
        <div className="copybox">
          <code data-testid="connect-mcp-url">{MCP_URL}</code>
          <button className="btn small" type="button" data-testid="connect-copy" onClick={copy}>
            <Icon name="copy" size={14} />
            Copy
          </button>
        </div>
        <div className="howto">
          <div className="howto-h">
            <h3>Add it to</h3>
            <div className="seg" role="group" aria-label="Which AI">
              <button type="button" aria-pressed={howto === "claude"} data-testid="connect-howto-claude" onClick={() => setHowto("claude")}>
                Claude
              </button>
              <button type="button" aria-pressed={howto === "chatgpt"} data-testid="connect-howto-chatgpt" onClick={() => setHowto("chatgpt")}>
                ChatGPT
              </button>
            </div>
          </div>
          <ol className="steps" data-testid={`connect-steps-${howto}`}>
            {HOWTO[howto].map((s, i) => (
              <li key={i}>
                {s.step}
                {s.aside ? <span className="aside">{s.aside}</span> : null}
              </li>
            ))}
          </ol>
          <p className="card-note">Claude and ChatGPT move these menus around. These steps follow their help pages as of Oct 17, 2026.</p>
        </div>
        <p className="card-note">{who}</p>
      </section>
      <div className="stack">
        <section className="card" data-testid="connect-cando">
          <div className="card-h">
            <h2>What it can and can&rsquo;t do</h2>
          </div>
          <p className="cando-h">It can</p>
          <ul className="cando">
            <CanDo icon="eye" tone="yes" title="See what you see" detail={readScope(me.user, dealer)} />
            <CanDo icon="pen-line" tone="yes" title="Make drafts as you" detail={`${WRITE_SCOPE} Each one is labeled, for example \u201cCreated by Claude for ${me.user.name}\u201d.`} />
          </ul>
          <p className="cando-h">It can&rsquo;t</p>
          <ul className="cando">
            <CanDo icon="send" tone="no" title="Send a QuoteLink, or mark a quote lost or sold" />
            <CanDo icon="badge-check" tone="no" title="Approve billing" />
            <CanDo icon="settings" tone="no" title="Change the catalog, dealers or people" />
          </ul>
          <p className="card-note">You do those in Omni. A draft it makes waits for you.</p>
        </section>
        <section className="card" data-testid="connect-own-system">
          <div className="card-h">
            <h2>Your own system</h2>
          </div>
          <p className="lede">A CRM or your own tool connects the same way, through Omni&rsquo;s API. It signs in through Omni and works as the person who allows it.</p>
          <div className="mini-facts">
            <div>
              <Icon name="lock" size={15} />
              <span>OAuth sign-in with your Omni email link. There are no API keys to copy.</span>
            </div>
            <div>
              <Icon name="clock" size={15} />
              <span>Its sign-in lasts an hour and renews while it keeps working. Unused for 30 days, it ends.</span>
            </div>
            <div>
              <Icon name="file-text" size={15} />
              <span>Everything it makes carries its name.</span>
            </div>
          </div>
          <div className="btnrow connect-docs">
            <button className="btn" type="button" data-testid="connect-api-docs" onClick={() => setDocs(true)}>
              <Icon name="code" size={15} />
              API documentation
            </button>
          </div>
        </section>
      </div>
      {docs ? <ApiDocsSheet onClose={() => setDocs(false)} /> : null}
    </div>
  );
}

/** Why a route is worth a second line in the table, where the scope alone does not say. */
const ROUTE_NOTES: Record<string, string> = {
  "PATCH /api/v1/quotes/:id": "drafts only",
  "POST /api/v1/quote-intakes": "a quote document for Omni to read",
};

function ApiDocsSheet({ onClose }: { onClose: () => void }): React.ReactElement {
  const routes = V1_ROUTES.filter((r) => r.requiredScope !== null);
  const openapi = `${OMNI_OAUTH_AUDIENCE_V1}/openapi.json`;
  return (
    <Sheet
      title="Omni API, version 1"
      wide
      onClose={onClose}
      foot={
        <button className="btn" type="button" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="apidocs" data-testid="api-docs">
        <p>Your system signs in the way Claude does: through Omni, as the person who allows it. There are no API keys.</p>
        <h3>Sign in</h3>
        <p>Register your app, send the person to Omni to allow it, then trade the code for tokens with your PKCE verifier. Omni describes every step here:</p>
        <code className="code-line">{`${OMNI_OAUTH_ISSUER}/.well-known/oauth-authorization-server`}</code>
        <p>
          Send the access token as a Bearer token on every call. It acts as that person, with their dealer, role and what they see. It lasts an hour; the refresh token renews it and changes each
          time you use it.
        </p>
        <h3>Calls</h3>
        <code className="code-line">{OMNI_OAUTH_AUDIENCE_V1}</code>
        <table className="routes">
          <tbody>
            {routes.map((r) => {
              const note = ROUTE_NOTES[`${r.method} ${r.path}`];
              return (
                <tr key={`${r.method} ${r.path}`} data-testid={`api-route-${r.method}-${r.path}`}>
                  <td className="m">
                    <span className={r.writeOperation ? "verb w" : "verb"}>{r.method}</span>
                  </td>
                  <td className="p">
                    {r.path.replace(/^\/api\/v1/, "")}
                    {note ? <span className="why">{note}</span> : null}
                  </td>
                  <td className="s">
                    <span className="code">{r.requiredScope}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="card-note">
          <span className="code">omni.read</span> reads what the person sees. <span className="code">omni.write</span> makes drafts: customers, contacts, quotes and quote documents. No call sends a QuoteLink,
          marks a quote lost or sold, or approves billing.
        </p>
        <h3>Errors</h3>
        <p>Every error is JSON with one field:</p>
        <code className="code-line">{'{"error": "Not one of yours."}'}</code>
        <p>401 means the sign-in was revoked or the person&rsquo;s Omni sign-in is turned off. 403 means the app wasn&rsquo;t allowed to make drafts. 404 means it isn&rsquo;t one of theirs.</p>
        <p className="card-note">
          Every field of every call:{" "}
          <a href={openapi} target="_blank" rel="noopener noreferrer">
            {openapi}
          </a>
        </p>
      </div>
    </Sheet>
  );
}

/** "Oct 14", "today at 10:14 AM", "yesterday at 5:20 PM", "12 min ago": a time after a verb. */
function after(iso: string): string {
  const text = whenIn(iso);
  return text.startsWith("on ") ? text.slice(3) : text;
}

interface People {
  me: Me;
  /** The person behind an email: the signed-in user always, anyone else once /api/users answered. */
  person(email: string): LensUser | null;
  dealerName(id: string | null): string | null;
}

/** One grant. `onRevoke` is null where the caller may not revoke it: someone else's, without grants.revoke over them. */
function GrantRow({ g, people, showWho, onRevoke }: { g: OAuthGrantRecord; people: People; showWho: boolean; onRevoke: ((g: OAuthGrantRecord) => void) | null }): React.ReactElement {
  const u = people.person(g.email);
  const name = u?.name ?? g.email;
  const off = !g.revokedAt && u?.status === "disabled";
  const meta = [showWho ? (u ? `${name}, ${roleLabel(u.role)}` : name) : null, `Allowed ${after(g.createdAt)}`, g.lastUsedAt ? `Last used ${after(g.lastUsedAt)}` : "Never used"].filter(Boolean).join(" \u00b7 ");
  const revokedWords = !g.revokedAt
    ? null
    : g.revokedBy === REVOKED_BY_REFRESH_REUSE
      ? `Revoked by Omni ${whenIn(g.revokedAt)}: an old sign-in token was used again`
      : `Revoked ${after(g.revokedAt)} by ${g.revokedBy === people.me.user.email ? "you" : g.revokedBy ? people.person(g.revokedBy)?.name ?? g.revokedBy : "Omni"}`;
  return (
    <li className={g.revokedAt ? "row grant revoked" : "row grant"} data-testid={`grant-${g.id}`} data-state={g.revokedAt ? "revoked" : off ? "turned-off" : "live"}>
      <AppTile off={Boolean(g.revokedAt) || off} />
      <div className="row-main">
        <span className="row-t">
          <b>{g.clientName}</b>
          <span className="host">{g.redirectHost}</span>
          {g.revokedAt ? <Chip kind="stop">Revoked</Chip> : off ? <Chip kind="warn">Sign-in turned off</Chip> : null}
        </span>
        <span className="row-m">{meta}</span>
        {off ? <span className="row-m grant-warn">Not working: {firstName(name)}&rsquo;s Omni sign-in is turned off, so every request is turned away.</span> : null}
        {revokedWords ? <span className="row-m">{revokedWords}</span> : null}
      </div>
      <div className="row-end">
        {g.revokedAt || !onRevoke ? null : (
          <button className="btn small danger" type="button" aria-label={`Revoke ${g.clientName} for ${name}`} data-testid={`grant-revoke-${g.id}`} onClick={() => onRevoke(g)}>
            Revoke
          </button>
        )}
      </div>
    </li>
  );
}

function GrantList({ list, people, showWho, indent, onRevoke }: { list: OAuthGrantRecord[]; people: People; showWho: boolean; indent?: boolean; onRevoke: ((g: OAuthGrantRecord) => void) | null }): React.ReactElement {
  return (
    <ul className={indent ? "rows grant-indent" : "rows"}>
      {orderGrants(list).map((g) => (
        <GrantRow key={g.id} g={g} people={people} showWho={showWho} onRevoke={onRevoke} />
      ))}
    </ul>
  );
}

function RevokeSheet({ g, people, onClose, onRevoked }: { g: OAuthGrantRecord; people: People; onClose: () => void; onRevoked: (next: OAuthGrantRecord) => void }): React.ReactElement {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const u = people.person(g.email);
  const name = u?.name ?? g.email;
  const own = g.email === people.me.user.email;
  const first = firstName(name);

  async function revoke(): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      onRevoked(await api.revokeOAuthGrant(g.id));
    } catch (err) {
      setProblem(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet
      title={`Revoke ${g.clientName}?`}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Keep it
          </button>
          <button className="btn stopfill" type="button" disabled={busy} data-testid="revoke-confirm" onClick={() => void revoke()}>
            {busy ? "Revoking" : "Revoke"}
          </button>
        </>
      }
    >
      <div data-testid="revoke-sheet">
        <AppReq client={g.clientName} line={`${g.redirectHost} \u00b7 works as ${name}${u ? `, ${roleAt(u.role, people.dealerName(u.dealerId))}` : ""}`} />
        <p className="card-p revoke-p">
          {g.clientName} stops working as {own ? "you" : first} at its next request. What it already made stays in Omni, still labeled &ldquo;Created by {g.clientName} for {name}&rdquo;.
        </p>
        <p className="card-note">{own ? `To use it again, connect it again from ${g.clientName}. Omni asks you to allow it first.` : `${first} can connect it again. Omni asks ${first} to allow it first.`}</p>
        {problem ? (
          <Callout kind="stop" icon="triangle-alert">
            {problem}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

function ConnectedApps({ grants }: { grants: Loaded<OAuthGrantRecord[]> }): React.ReactElement {
  const { me, api, writeScope } = useSession();
  const toast = useToast();
  // Anyone revokes their own apps. grants.revoke is for other people's: an owner's at their dealer, a Super admin's anywhere.
  const kind = viewerKind(me.user);
  let ops: boolean;
  let owner: boolean;
  switch (kind) {
    case "admin":
      ops = true;
      owner = false;
      break;
    case "dealer":
      ops = false;
      owner = writeScope("grants.revoke") === "own" && writeScope("users.manage") === "own";
      break;
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
  const revokesAny = writeScope("grants.revoke") === "any";
  const users = useLoad<LensUser[]>(() => (owner || ops ? api.users() : Promise.resolve([])), [owner, ops]);
  const dealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  const [revoking, setRevoking] = useState<OAuthGrantRecord | null>(null);
  const [filter, setFilter] = useState("");

  const people: People = {
    me,
    person: (email) => (email === me.user.email ? me.user : users.data?.find((u) => u.email === email) ?? null),
    dealerName: (id) => (id === null ? null : id === me.dealer?.id ? me.dealer.name : dealers.data?.find((d) => d.id === id)?.name ?? null),
  };
  const all = grants.data ?? [];
  const mine = all.filter((g) => g.email === me.user.email);
  const onRevoke = (g: OAuthGrantRecord) => setRevoking(g);

  if (grants.error) {
    return (
      <Callout kind="stop" icon="triangle-alert">
        {grants.error.message}
      </Callout>
    );
  }
  if (!grants.data) {
    return (
      <section className="card">
        <div className="empty">Loading connected apps.</div>
      </section>
    );
  }

  const live = mine.filter((g) => !g.revokedAt);
  const gone = mine.filter((g) => g.revokedAt);
  const connectLink = (
    <Link to="/settings/connect" data-testid="apps-connect-link">
      Connect an AI or app
    </Link>
  );
  const ownCard = (
    <section className="card apps-card" data-testid="apps-own">
      <div className="card-h">
        <h2>Apps that work as you</h2>
        <span className="note">
          As {me.user.name}, {roleAt(me.user.role, me.dealer?.name ?? null)}
        </span>
      </div>
      {mine.length === 0 ? (
        <div className="empty" data-testid="apps-own-empty">
          Nothing works as you yet. {connectLink}
        </div>
      ) : (
        <>
          {live.length ? (
            <GrantList list={live} people={people} showWho={false} onRevoke={onRevoke} />
          ) : (
            <div className="empty" data-testid="apps-own-empty">
              Nothing works as you now. {connectLink}
            </div>
          )}
          {gone.length ? (
            <>
              <div className="sub-h">
                <h3>Revoked</h3>
              </div>
              <GrantList list={gone} people={people} showWho={false} onRevoke={onRevoke} />
            </>
          ) : null}
        </>
      )}
    </section>
  );

  let othersCard: React.ReactNode = null;
  if (owner && me.dealer) {
    const others = (users.data ?? []).filter((u) => u.dealerId === me.dealer?.id && u.email !== me.user.email).sort((a, b) => a.name.localeCompare(b.name));
    const withApps = others.filter((u) => all.some((g) => g.email === u.email));
    const none = others.filter((u) => !withApps.includes(u));
    const working = all.filter((g) => !g.revokedAt && others.some((u) => u.email === g.email)).length;
    othersCard = (
      <section className="card apps-card" data-testid="apps-dealer">
        <div className="card-h">
          <h2>Everyone at {me.dealer.name}</h2>
          <span className="note">{plural(working, "app")} working as your people</span>
        </div>
        {users.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {users.error.message}
          </Callout>
        ) : !users.data ? (
          <div className="empty">Loading people.</div>
        ) : (
          <>
            {withApps.map((u) => (
              <div key={u.email} data-testid={`apps-person-${u.email}`}>
                <div className="grant-who">
                  <Avatar name={u.name} />
                  <span>
                    <b>{u.name}</b> <span className="sub">{roleLabel(u.role)}</span>
                  </span>
                  {u.status === "disabled" ? <Chip kind="stop">Turned off</Chip> : null}
                </div>
                <GrantList list={all.filter((g) => g.email === u.email)} people={people} showWho={false} indent onRevoke={onRevoke} />
              </div>
            ))}
            <p className="card-note">
              {none.length ? `${new Intl.ListFormat("en", { type: "conjunction" }).format(none.map((u) => u.name))} ${none.length === 1 ? "has" : "have"} nothing connected. ` : ""}
              Only the person can connect an app as themselves. You can revoke any of these.
            </p>
          </>
        )}
      </section>
    );
  }

  if (ops) {
    const others = all.filter((g) => g.email !== me.user.email);
    const dealerOf = (g: OAuthGrantRecord) => people.person(g.email)?.dealerId ?? null;
    const scoped = filter ? others.filter((g) => dealerOf(g) === filter) : others;
    const on = scoped.filter((g) => !g.revokedAt).length;
    const sortedDealers = [...(dealers.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const groups: Array<{ key: string; name: string; list: OAuthGrantRecord[] }> = sortedDealers
      .map((d) => ({ key: d.id, name: d.name, list: scoped.filter((g) => dealerOf(g) === d.id) }))
      .filter((grp) => grp.list.length > 0);
    if (!filter) {
      // Another Phoneware person's apps, and anyone Omni no longer lists: still revocable here.
      const opsPeople = scoped.filter((g) => isAdminRole(people.person(g.email)?.role ?? ""));
      const unknown = scoped.filter((g) => !people.person(g.email) || (dealerOf(g) !== null && !sortedDealers.some((d) => d.id === dealerOf(g))));
      if (opsPeople.length) groups.push({ key: "ops", name: "Phoneware", list: opsPeople });
      if (unknown.length) groups.push({ key: "unknown", name: "Not in Omni any more", list: unknown });
    }
    const filterName = sortedDealers.find((d) => d.id === filter)?.name ?? "";
    othersCard = (
      <section className="card apps-card" data-testid="apps-every-dealer">
        <div className="card-h">
          <h2>Every dealer</h2>
          <span className="note">{revokesAny ? "Revoke any of them" : "A Super admin revokes them"}</span>
        </div>
        <div className="grant-filter">
          <label className="sr" htmlFor="grant-dealer">
            Dealer
          </label>
          <select className="input" id="grant-dealer" data-testid="apps-dealer-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All dealers</option>
            {sortedDealers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <span className="note" data-testid="apps-dealer-counts">
            {on} connected &middot; {scoped.length - on} revoked
          </span>
        </div>
        {!users.data || !dealers.data ? (
          users.error || dealers.error ? (
            <Callout kind="stop" icon="triangle-alert">
              {(users.error ?? dealers.error)?.message}
            </Callout>
          ) : (
            <div className="empty">Loading people.</div>
          )
        ) : groups.length === 0 ? (
          <div className="empty" data-testid="apps-dealer-empty">
            {filter ? `No apps work as anyone at ${filterName}.` : "No apps work as anyone at a dealer yet."}
          </div>
        ) : (
          groups.map((grp) => {
            const n = grp.list.filter((g) => !g.revokedAt).length;
            return (
              <div key={grp.key} data-testid={`apps-group-${grp.key}`}>
                {filter ? null : (
                  <div className="grant-dealer">
                    <h3>{grp.name}</h3>
                    <span className="note">
                      {n} connected{grp.list.length > n ? ` \u00b7 ${grp.list.length - n} revoked` : ""}
                    </span>
                  </div>
                )}
                <GrantList list={grp.list} people={people} showWho onRevoke={revokesAny ? onRevoke : null} />
              </div>
            );
          })
        )}
      </section>
    );
  }

  return (
    <>
      {ownCard}
      {othersCard}
      {revoking ? (
        <RevokeSheet
          g={revoking}
          people={people}
          onClose={() => setRevoking(null)}
          onRevoked={(next) => {
            setRevoking(null);
            grants.set(all.map((g) => (g.id === next.id ? next : g)));
            const own = next.email === me.user.email;
            toast(`Revoked. ${next.clientName}\u2019s next request ${own ? "as you" : `as ${people.person(next.email)?.name ?? next.email}`} is turned away.`);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Settings -> Your accounts (mock M3 states 01, 04, 05, 07 to 11 and 13): the reader's account in each product Phoneware
 * sells, why Omni thinks so, and how to sign in where Omni can't look. Only the reader says an account isn't theirs.
 */
function YourAccounts({ accounts }: { accounts: Loaded<PersonAccounts> }): React.ReactElement {
  const { me, api, writeScope } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [addingMicrosoft, setAddingMicrosoft] = useState(false);
  const person = me.user;
  const kind = viewerKind(person);
  let seers: string;
  switch (kind) {
    case "admin":
      seers = "Other Phoneware admins can";
      break;
    case "dealer":
      seers = writeScope("users.manage") === "own" ? "Phoneware can" : `Your owner at ${me.dealer?.name ?? "your dealer"} and Phoneware can`;
      break;
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }

  async function act(run: () => Promise<PersonAccounts>, said: (next: PersonAccounts) => string): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      const next = await run();
      accounts.set(next);
      toast(said(next));
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const actions: RowActions = {
    busy,
    notMe: (a) => void act(() => api.dismissAccount(person.email, a.matchId), () => `Omni won\u2019t suggest ${a.key} again.`),
    undo: (key) => void act(() => api.undoAccount(person.email, key), () => "Back as a suggestion."),
    // Only the person adds their own Microsoft sign-in: never while someone acts as them.
    addMicrosoft: me.actingAs ? null : () => setAddingMicrosoft(true),
  };

  // What a check found, row by row, so Check again can say when nothing changed.
  const found = (a: PersonAccounts) => a.rows.map((r) => `${r.product}:${r.state}:${r.accounts.map((x) => x.matchId).join(",")}`).join("|");
  const andList = (names: string[]) => (names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names.join(""));
  function checkAgain(): void {
    const before = accounts.data ? found(accounts.data) : null;
    void act(
      () => api.refreshPersonAccounts(person.email),
      (next) => {
        // Only the products Omni reached were checked; one it couldn't reach kept what it had.
        const searched = next.rows.filter((r) => !signsInThere(r)).map((r) => r.product);
        const reached = searched.filter((p) => !next.unavailable.includes(p)).map((p) => PRODUCT[p].name);
        const missed = searched.filter((p) => next.unavailable.includes(p)).map((p) => PRODUCT[p].name);
        if (reached.length === 0) return `Omni couldn\u2019t reach ${andList(missed)} just now. Check again in a minute.`;
        const after = missed.length ? ` Omni couldn\u2019t reach ${andList(missed)}.` : before === found(next) ? " Nothing new." : "";
        return `Checked ${andList(reached)} for ${person.email} just now.${after}`;
      },
    );
  }

  const n = accounts.data ? accountCounts(accounts.data) : null;
  return (
    <section className="card accounts-card" data-testid="your-accounts">
      <div className="card-h wrap">
        <h2>Your accounts</h2>
        <span className="note">
          {person.name}, {person.email}
        </span>
        <button className="btn small" type="button" data-testid="accounts-check-again" disabled={busy || !accounts.data} onClick={checkAgain}>
          <Icon name="refresh-cw" size={13} />
          Check again
        </button>
      </div>
      {problem ? (
        <Callout kind="stop" icon="triangle-alert">
          {problem}
        </Callout>
      ) : null}
      {accounts.error ? (
        <Callout kind="stop" icon="triangle-alert">
          {accounts.error.message}
        </Callout>
      ) : accounts.data && n ? (
        <>
          <p className="lede">
            Every product Phoneware sells, and the account each one holds for you. Omni looks for the first {numberWord(n.searched)} itself.
            {n.there === 1 ? " The other one keeps its own sign-in, so this shows how to get in." : n.there ? ` The other ${numberWord(n.there)} keep their own sign-in, so this shows how to get in.` : ""}
          </p>
          <SummaryChips accounts={accounts.data} />
          <AccountGroups accounts={accounts.data} person={person} self actions={actions} />
        </>
      ) : (
        <div className="empty">Loading your accounts.</div>
      )}
      {/* #1660 adds linking back: "Only you can link an account or say one isn't yours." */}
      <p className="card-note">{seers} see this list. Only you can say an account isn&rsquo;t yours.</p>
      {addingMicrosoft ? (
        <AddMicrosoftSheet
          onClose={() => setAddingMicrosoft(false)}
          onAdded={async () => {
            setAddingMicrosoft(false);
            await act(() => api.refreshPersonAccounts(person.email), () => "Microsoft sign-in added. Your Microsoft 365 account shows here now.");
          }}
        />
      ) : null}
    </section>
  );
}

const TAB_TITLE: Record<SettingsTab, string> = {
  general: "Settings",
  activity: "Phoneware activity",
  signin: "Sign-in",
  accounts: "Your accounts",
  connect: "Connect an AI or app",
  apps: "Connected apps",
};

export function Settings({ tab }: { tab: SettingsTab }): React.ReactElement {
  const { me, api } = useSession();
  useTitle(TAB_TITLE[tab]);
  const grants = useLoad<OAuthGrantRecord[]>(() => api.oauthGrants(), []);
  // The reader's own accounts: Your accounts shows them, and the tab counts what waits on them.
  const own = useLoad<PersonAccounts>(() => api.personAccounts(me.user.email), [me.user.email]);
  const dealer = me.dealer;
  const kind = viewerKind(me.user);
  const live = grants.data ? grants.data.filter((g) => !g.revokedAt).length : null;
  let general: string;
  switch (kind) {
    case "admin":
      general = "Phoneware";
      break;
    case "dealer":
      general = dealer?.name ?? "Your dealer";
      break;
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
  const tabs = <SettingsTabs tab={tab} general={general} live={live} acting={!!me.actingAs} />;
  if (tab === "general") {
    switch (kind) {
      case "admin":
        return <AdminSettings tabs={tabs} />;
      case "dealer":
        return dealer ? (
          <DealerSettings dealer={dealer} tabs={tabs} />
        ) : (
          <>
            <Head title="Settings" sub={settingsSub(kind, null)} />
            {tabs}
            <section className="card">
              <div className="empty">No dealer is assigned to this user.</div>
            </section>
          </>
        );
      default: {
        const unhandled: never = kind;
        throw new Error(`Unknown viewer ${String(unhandled)}`);
      }
    }
  }
  return (
    <>
      <Head title="Settings" sub={settingsSub(kind, dealer, !!me.actingAs)} />
      {tabs}
      {tab === "signin" ? (
        me.actingAs ? <SignInHidden name={me.actingAs.target.name} /> : <SignInTab />
      ) : tab === "activity" ? (
        <ActivityTab />
      ) : tab === "accounts" ? (
        <YourAccounts accounts={own} />
      ) : tab === "connect" ? (
        me.actingAs ? <ConnectOff name={me.actingAs.target.name} /> : <ConnectTab />
      ) : (
        <ConnectedApps grants={grants} />
      )}
    </>
  );
}
